// src/App.tsx
// Root application component. Sets up React Router, AuthContext, and ToastContext.
// Routes are wrapped with Sentry.withSentryReactRouterV6Routing for route-level
// performance tracing - each navigation produces a named Sentry transaction.
// Updated: added /admin/health-check route for post-deployment RLS verification.
// Updated Jun 2026: added ImpersonationProvider (wraps inside AuthProvider so it can read
// the real user's ID via useAuth) to support admin "view as user" mode.
// Updated Jun 2026: removed /admin/catalog route (catalog system removed).

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AdminGuard } from './components/auth/AdminGuard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ClientCollect from './pages/ClientCollect';
import SharedFormLanding from './pages/SharedFormLanding';
import AdminHealthCheck from './pages/admin/AdminHealthCheck';
import AdminUsers from './pages/AdminUsers';
import MyForms from './pages/MyForms';
import CreateFormTemplate from './pages/CreateFormTemplate';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Settings from './pages/Settings';
import AuthNotification from './pages/AuthNotification';
import AcceptInvite from './pages/AcceptInvite';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function ImpersonationWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return (
    <ImpersonationProvider realUserId={user?.id ?? null}>
      {children}
    </ImpersonationProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <ImpersonationWrapper>
            <SentryRoutes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/c/:token" element={<ClientCollect />} />
            <Route path="/f/:shareToken" element={<SharedFormLanding />} />
            <Route path="/auth/notification" element={<AuthNotification />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminUsers />
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/health-check"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminHealthCheck />
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-forms"
              element={
                <ProtectedRoute>
                  <MyForms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-forms/new"
              element={
                <ProtectedRoute>
                  <CreateFormTemplate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-forms/:draftId/draft"
              element={
                <ProtectedRoute>
                  <CreateFormTemplate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-forms/:id/edit"
              element={
                <ProtectedRoute>
                  <CreateFormTemplate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <Clients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id"
              element={
                <ProtectedRoute>
                  <ClientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            </SentryRoutes>
          </ImpersonationWrapper>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
